﻿import * as React from "react";
import Draggable, { DraggableData } from "react-draggable";
import { Button } from "../Components/Button";
import { IReturnModel } from "../IReturnModel";

interface ICoordinate {
    x: number;
    y: number;
}

export interface IImageCropperProps {
    imageAsDataUri: string;
    imageResolution: ICoordinate;
    updateImage: (imageUri: string) => void;
}

export interface IImageCropperState {
    centerDimensions: ICoordinate;
    centralControlPoint: ICoordinate;
    minDimensions: ICoordinate;
    neControlPoint: ICoordinate;
    nwControlPoint: ICoordinate;
    seControlPoint: ICoordinate;
    swControlPoint: ICoordinate;
}

export class ImageCropper extends React.Component<
    IImageCropperProps,
    IImageCropperState
> {
    constructor(props: IImageCropperProps) {
        super(props);

        this.state = {
            centerDimensions: {
                x: 180,
                y: 180,
            },
            centralControlPoint: {
                x: 0,
                y: 0,
            },
            minDimensions: {
                x: 180,
                y: 180,
            },
            neControlPoint: {
                x: 0,
                y: 0,
            },
            nwControlPoint: {
                x: 0,
                y: 0,
            },
            seControlPoint: {
                x: 0,
                y: 0,
            },
            swControlPoint: {
                x: 0,
                y: 0,
            },
        };
    }

    componentDidMount(): void {
        const proportionX: number = 180 / this.props.imageResolution.x,
            proportionY: number = 180 / this.props.imageResolution.y;

        const actualX: number = this._windowRef.clientWidth * proportionX,
            actualY: number = this._windowRef.clientHeight * proportionY;

        const actualAbs: number = Math.min(actualX, actualY);

        this.setState({
            minDimensions: {
                x: actualAbs,
                y: actualAbs,
            },
        });

        window.addEventListener(`resize`, this.checkPositioning);
    }

    componentWillUnmount(): void {
        window.removeEventListener(`resize`, this.checkPositioning);
    }

    private _windowRef: HTMLDivElement;

    onDrag = (e: MouseEvent, ui: DraggableData): void => {
        this.setState((prevState) => ({
            centralControlPoint: {
                x: prevState.centralControlPoint.x + ui.deltaX,
                y: prevState.centralControlPoint.y + ui.deltaY,
            },
        }));
    };

    onDragEnd = (): void => {
        this.setState({
            neControlPoint: {
                x: 0,
                y: 0,
            },
            nwControlPoint: {
                x: 0,
                y: 0,
            },
            seControlPoint: {
                x: 0,
                y: 0,
            },
            swControlPoint: {
                x: 0,
                y: 0,
            },
        });

        this.checkPositioning();
    };

    checkPositioning = (): void => {
        this.setState((prevState) => {
            let dimX: number = prevState.centerDimensions.x,
                dimY: number = prevState.centerDimensions.y;

            if (
                prevState.centerDimensions.x > this._windowRef.clientWidth ||
                prevState.centerDimensions.y > this._windowRef.clientHeight
            ) {
                dimY = Math.min(
                    this._windowRef.clientWidth,
                    this._windowRef.clientHeight,
                );
                dimX = Math.min(
                    this._windowRef.clientWidth,
                    this._windowRef.clientHeight,
                );
            }

            let centerX: number = prevState.centralControlPoint.x,
                centerY: number = prevState.centralControlPoint.y;

            if (
                prevState.centerDimensions.x + prevState.centralControlPoint.x >
                this._windowRef.clientWidth
            ) {
                centerX =
                    this._windowRef.clientWidth - prevState.centerDimensions.x;
            }

            if (prevState.centralControlPoint.x < 0) {
                centerX = 0;
            }

            if (
                prevState.centerDimensions.y + prevState.centralControlPoint.y >
                this._windowRef.clientHeight
            ) {
                centerY =
                    this._windowRef.clientHeight - prevState.centerDimensions.y;
            }

            if (prevState.centralControlPoint.y < 0) {
                centerY = 0;
            }

            return {
                centerDimensions: {
                    x: dimX,
                    y: dimY,
                },
                centralControlPoint: {
                    x: centerX,
                    y: centerY,
                },
            };
        });
    };

    nwOnDrag = (e: MouseEvent, ui: DraggableData): void => {
        const absDelta: number = (0 - ui.deltaX - ui.deltaY) / 2,
            lastCornerPos: ICoordinate = this.state.nwControlPoint,
            lastPos: ICoordinate = this.state.centralControlPoint,
            lastSize: ICoordinate = this.state.centerDimensions;

        // If the size is not zero or it is being made bigger.
        if (
            (this.state.centerDimensions.x > this.state.minDimensions.x &&
                this.state.centerDimensions.y > this.state.minDimensions.y) ||
            absDelta > 0
        ) {
            // If the height is not taller than it's container or is being made smaller.
            if (
                this.state.centerDimensions.y -
                    this.state.centralControlPoint.y <
                    this._windowRef.clientHeight ||
                absDelta < 0
            ) {
                // If the width is not wider than it's container or is being made smaller.
                if (
                    this.state.centerDimensions.x -
                        this.state.centralControlPoint.x <
                        this._windowRef.clientWidth ||
                    absDelta < 0
                ) {
                    this.setState({
                        centerDimensions: {
                            x: lastSize.x + absDelta,
                            y: lastSize.y + absDelta,
                        },
                        centralControlPoint: {
                            x: lastPos.x - absDelta,
                            y: lastPos.y - absDelta,
                        },
                        nwControlPoint: {
                            x: lastCornerPos.x + ui.deltaX - absDelta,
                            y: lastCornerPos.y + ui.deltaY - absDelta,
                        },
                    });
                }
            }
        }

        this.checkPositioning();
    };

    neOnDrag = (e: MouseEvent, ui: DraggableData): void => {
        const absDelta: number = (ui.deltaX - ui.deltaY) / 2,
            lastCornerPos: ICoordinate = this.state.neControlPoint,
            lastPos: ICoordinate = this.state.centralControlPoint,
            lastSize: ICoordinate = this.state.centerDimensions;

        // If the size is not zero or it is being made bigger.
        if (
            (this.state.centerDimensions.x > this.state.minDimensions.x &&
                this.state.centerDimensions.y > this.state.minDimensions.y) ||
            absDelta > 0
        ) {
            // If the height is not taller than it's container or is being made smaller.
            if (
                this.state.centerDimensions.y -
                    this.state.centralControlPoint.y <
                    this._windowRef.clientHeight ||
                absDelta < 0
            ) {
                // If the width is not wider than it's container or is being made smaller.
                if (
                    this.state.centerDimensions.x -
                        this.state.centralControlPoint.x <
                        this._windowRef.clientWidth ||
                    absDelta < 0
                ) {
                    this.setState({
                        centerDimensions: {
                            x: lastSize.x + absDelta,
                            y: lastSize.y + absDelta,
                        },
                        centralControlPoint: {
                            x: lastPos.x,
                            y: lastPos.y - absDelta,
                        },
                        neControlPoint: {
                            x: lastCornerPos.x + ui.deltaX - absDelta,
                            y: lastCornerPos.y + ui.deltaY,
                        },
                    });
                }
            }
        }

        this.checkPositioning();
    };

    swOnDrag = (e: MouseEvent, ui: DraggableData): void => {
        const absDelta: number = (0 - ui.deltaX + ui.deltaY) / 2,
            lastCornerPos: ICoordinate = this.state.swControlPoint,
            lastPos: ICoordinate = this.state.centralControlPoint,
            lastSize: ICoordinate = this.state.centerDimensions;

        // If the size is not zero or it is being made bigger.
        if (
            (this.state.centerDimensions.x > this.state.minDimensions.x &&
                this.state.centerDimensions.y > this.state.minDimensions.y) ||
            absDelta > 0
        ) {
            // If the height is not taller than it's container or is being made smaller.
            if (
                this.state.centerDimensions.y -
                    this.state.centralControlPoint.y <
                    this._windowRef.clientHeight ||
                absDelta < 0
            ) {
                // If the width is not wider than it's container or is being made smaller.
                if (
                    this.state.centerDimensions.x -
                        this.state.centralControlPoint.x <
                        this._windowRef.clientWidth ||
                    absDelta < 0
                ) {
                    this.setState({
                        centerDimensions: {
                            x: lastSize.x + absDelta,
                            y: lastSize.y + absDelta,
                        },
                        centralControlPoint: {
                            x: lastPos.x - absDelta,
                            y: lastPos.y,
                        },
                        swControlPoint: {
                            x: lastCornerPos.x + ui.deltaX - absDelta,
                            y: lastCornerPos.y + ui.deltaY,
                        },
                    });
                }
            }
        }

        this.checkPositioning();
    };

    seOnDrag = (e: MouseEvent, ui: DraggableData): void => {
        const absDelta: number = (ui.deltaX + ui.deltaY) / 2,
            lastCornerPos: ICoordinate = this.state.seControlPoint,
            lastPos: ICoordinate = this.state.centralControlPoint,
            lastSize: ICoordinate = this.state.centerDimensions;

        // If the size is not zero or it is being made bigger.
        if (
            (this.state.centerDimensions.x > this.state.minDimensions.x &&
                this.state.centerDimensions.y > this.state.minDimensions.y) ||
            absDelta > 0
        ) {
            // If the height is not taller than it's container or is being made smaller.
            if (
                this.state.centerDimensions.y -
                    this.state.centralControlPoint.y <
                    this._windowRef.clientHeight ||
                absDelta < 0
            ) {
                // If the width is not wider than it's container or is being made smaller.
                if (
                    this.state.centerDimensions.x -
                        this.state.centralControlPoint.x <
                        this._windowRef.clientWidth ||
                    absDelta < 0
                ) {
                    this.setState({
                        centerDimensions: {
                            x: lastSize.x + absDelta,
                            y: lastSize.y + absDelta,
                        },
                        centralControlPoint: {
                            x: lastPos.x,
                            y: lastPos.y,
                        },
                        seControlPoint: {
                            x: lastCornerPos.x + ui.deltaX,
                            y: lastCornerPos.y + ui.deltaY,
                        },
                    });
                }
            }
        }

        this.checkPositioning();
    };

    cropOnClick = (): void => {
        void this.postResult();
    };

    postResult = async (): Promise<void> => {
        const actualDim: number =
                (this.state.centerDimensions.x / this._windowRef.clientWidth) *
                this.props.imageResolution.x,
            actualX: number =
                (this.state.centralControlPoint.x /
                    this._windowRef.clientWidth) *
                this.props.imageResolution.x,
            actualY: number =
                (this.state.centralControlPoint.y /
                    this._windowRef.clientHeight) *
                this.props.imageResolution.y,
            stringResult: string = await $.ajax({
                data: {
                    cropDimension: actualDim,
                    cropX: actualX,
                    cropY: actualY,
                    image: this.props.imageAsDataUri,
                },
                method: `POST`,
                url: `/Profile/GetProfileImageURI`,
            });

        this.props.updateImage(stringResult);
    };

    render(): React.ReactElement {
        return (
            <div className={`cropcomponent`}>
                <div
                    className={`cropcomponent-imagecropper`}
                    ref={(aa): HTMLDivElement => (this._windowRef = aa)}
                >
                    <div className={`imagecropper-imagecontainer`}>
                        <Draggable
                            bounds={`parent`}
                            handle={`.imagecropper-draggable`}
                            onDrag={this.onDrag}
                            onStop={this.onDragEnd}
                            position={this.state.centralControlPoint}
                        >
                            <div className={`cropdrag-container`}>
                                {/* Row One */}
                                <div className={`container-row`}>
                                    <div className={`row-cornercontainer`}>
                                        <Draggable
                                            handle={`.cornercontainer-dragcorner`}
                                            onDrag={this.nwOnDrag}
                                            onStop={this.onDragEnd}
                                            position={this.state.nwControlPoint}
                                        >
                                            <div
                                                className={`cornercontainer-dragcorner nw`}
                                            />
                                        </Draggable>
                                        <div
                                            className={`cornercontainer-dragicon nw`}
                                        />
                                    </div>
                                    <div className={`row-dragtop`} />
                                    <div className={`row-cornercontainer`}>
                                        <Draggable
                                            handle={`.cornercontainer-dragcorner`}
                                            onDrag={this.neOnDrag}
                                            onStop={this.onDragEnd}
                                            position={this.state.neControlPoint}
                                        >
                                            <div
                                                className={`cornercontainer-dragcorner ne`}
                                            />
                                        </Draggable>
                                        <div
                                            className={`cornercontainer-dragicon ne`}
                                        />
                                    </div>
                                </div>
                                {/* Row Two */}
                                <div className={`container-row`}>
                                    <div className={`row-dragside`} />
                                    <div
                                        className={`imagecropper-draggable`}
                                        style={{
                                            height: this.state.centerDimensions
                                                .y,
                                            width: this.state.centerDimensions
                                                .x,
                                        }}
                                    />
                                    <div className={`row-dragside`} />
                                </div>
                                {/* Row Three */}
                                <div className={`container-row`}>
                                    <div className={`row-cornercontainer`}>
                                        <Draggable
                                            handle={`.cornercontainer-dragcorner`}
                                            onDrag={this.swOnDrag}
                                            onStop={this.onDragEnd}
                                            position={this.state.swControlPoint}
                                        >
                                            <div
                                                className={`cornercontainer-dragcorner sw`}
                                            />
                                        </Draggable>
                                        <div
                                            className={`cornercontainer-dragicon sw`}
                                        />
                                    </div>
                                    <div className={`row-dragbottom`} />
                                    <div className={`row-cornercontainer`}>
                                        <Draggable
                                            handle={`.cornercontainer-dragcorner`}
                                            onDrag={this.seOnDrag}
                                            onStop={this.onDragEnd}
                                            position={this.state.seControlPoint}
                                        >
                                            <div
                                                className={`cornercontainer-dragcorner se`}
                                            />
                                        </Draggable>
                                        <div
                                            className={`cornercontainer-dragicon se`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Draggable>
                        <img src={this.props.imageAsDataUri} />
                    </div>
                </div>
                <div className={`cropcomponent-controlcontainer`}>
                    <Button
                        className={`controlcontainer-cropbutton`}
                        onClick={this.cropOnClick}
                        text={`Crop`}
                    />
                </div>
            </div>
        );
    }
}
